import { Link } from 'react-router';
import { ThemeToggle } from '@mieweb/ui';
import { BrandLockup } from '../BrandMark';
import { PageMeta } from '../PageMeta';
import { StructuredData } from '../StructuredData';
import { MarketingFooter } from '../MarketingFooter';
import { ComparisonHero } from './ComparisonHero';
import { FeatureComparisonTable } from './FeatureComparisonTable';
import { PricingComparison } from './PricingComparison';
import { WhySwitch } from './WhySwitch';
import { ComparisonFAQ } from './ComparisonFAQ';
import { ComparisonCTA } from './ComparisonCTA';
import type { CompetitorData } from '../../data/competitors';
import { useMemo } from 'react';

interface ComparisonLayoutProps {
  competitor: CompetitorData;
}

export function ComparisonLayout({ competitor }: ComparisonLayoutProps) {
  const pageUrl = `https://pollenate.dev/vs/${competitor.slug}`;

  // Memoize structured data to avoid re-creating objects on every render
  const webPageData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: competitor.metaTitle,
    description: competitor.metaDescription,
    url: pageUrl,
  }), [competitor.metaTitle, competitor.metaDescription, pageUrl]);

  const breadcrumbData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://pollenate.dev/' },
      { '@type': 'ListItem', position: 2, name: 'Compare', item: 'https://pollenate.dev/vs' },
      { '@type': 'ListItem', position: 3, name: `Pollenate vs ${competitor.name}`, item: pageUrl },
    ],
  }), [competitor.name, pageUrl]);

  const productData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Pollenate',
    description: 'Collect and analyze user feedback with embeddable widgets, feedback pages, and AI-powered insights.',
    brand: { '@type': 'Brand', name: 'Pollenate' },
    url: 'https://pollenate.dev',
    offers: [
      { '@type': 'Offer', price: '0', priceCurrency: 'USD', name: 'Starter', description: '5,000 events/month, 1 inbox, all widget types' },
      { '@type': 'Offer', price: '49', priceCurrency: 'USD', name: 'Team', description: '100,000 events/month, 5 inboxes, AI insights' },
    ],
  }), []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 dark:from-neutral-950 to-white dark:to-neutral-900">
      {/* SEO */}
      <PageMeta
        title={competitor.metaTitle}
        description={competitor.metaDescription}
        url={pageUrl}
      />
      <StructuredData id={`vs-${competitor.slug}-webpage`} data={webPageData} />
      <StructuredData id={`vs-${competitor.slug}-breadcrumb`} data={breadcrumbData} />
      <StructuredData id={`vs-${competitor.slug}-product`} data={productData} />

      {/* Navigation */}
      <nav className="bg-white dark:bg-neutral-900 border-b border-slate-200 dark:border-neutral-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <BrandLockup
                iconClassName="h-7 w-7"
                textClassName="text-xl font-bold text-slate-900 dark:text-white"
              />
            </Link>
            <div className="flex items-center gap-3">
              <ThemeToggle mode="two-way" size="sm" variant="ghost" />
              <Link
                to="/vs"
                className="text-slate-600 dark:text-neutral-400 hover:text-slate-900 dark:hover:text-white transition-colors text-sm"
              >
                Compare
              </Link>
              <Link
                to="/docs"
                className="text-slate-600 dark:text-neutral-400 hover:text-slate-900 dark:hover:text-white transition-colors text-sm"
              >
                Docs
              </Link>
              <Link
                to="/login"
                className="text-slate-600 dark:text-neutral-400 hover:text-slate-900 dark:hover:text-white transition-colors text-sm"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <ComparisonHero
        competitorName={competitor.name}
        competitorEmoji={competitor.logoEmoji}
        competitorCategory={competitor.category}
        tagline={competitor.description}
      />

      {/* Feature Comparison */}
      <FeatureComparisonTable
        features={competitor.features}
        competitorName={competitor.name}
      />

      {/* Pricing */}
      <PricingComparison
        competitorName={competitor.name}
        competitorPricing={competitor.pricing}
        competitorPricingUrl={competitor.pricingUrl}
      />

      {/* Why Switch */}
      <WhySwitch
        competitorName={competitor.name}
        switchReasons={competitor.switchReasons}
        prosVsPollenate={competitor.prosVsPollenate}
        consVsPollenate={competitor.consVsPollenate}
      />

      {/* FAQ */}
      <ComparisonFAQ
        faqs={competitor.faqs}
        competitorName={competitor.name}
      />

      {/* Final CTA */}
      <ComparisonCTA competitorName={competitor.name} />

      {/* Footer */}
      <MarketingFooter />
    </div>
  );
}
